import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Banner,
  Button,
  Container,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
} from 'wss3-forge'
import { listTraces, type Trace, type TraceKind } from '../lib/api.js'

type BannerVariant = 'info' | 'success' | 'warning' | 'error' | 'neutral' | 'brand'

const KIND_VARIANT: Record<TraceKind, BannerVariant> = {
  llm_call: 'brand',
  tool_call: 'info',
  recall: 'success',
  embedding: 'neutral',
}

function formatDuration(ms: number | null): string {
  if (ms == null) return '...'
  if (ms < 1000) return `${ms} ms`
  return `${(ms / 1000).toFixed(1)} s`
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const sec = Math.round(diff / 1000)
  if (sec < 60) return `il y a ${sec}s`
  if (sec < 3600) return `il y a ${Math.round(sec / 60)} min`
  if (sec < 86400) return `il y a ${Math.round(sec / 3600)} h`
  return new Date(iso).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function compactJson(v: unknown, max = 160): string {
  if (v == null) return ''
  try {
    const s = JSON.stringify(v)
    return s.length > max ? s.slice(0, max - 1) + '…' : s
  } catch {
    return String(v).slice(0, max)
  }
}

export function TracesPage() {
  const [errorsOnly, setErrorsOnly] = useState(false)

  const tracesQuery = useQuery<Trace[]>({
    queryKey: ['traces', errorsOnly],
    queryFn: () => listTraces({ limit: 200, errorsOnly }),
    refetchInterval: 5_000,
  })

  const traces = tracesQuery.data ?? []

  // Group by trace_id so we see one block per turn.
  const groups = new Map<string, Trace[]>()
  for (const t of traces) {
    const arr = groups.get(t.trace_id) ?? []
    arr.push(t)
    groups.set(t.trace_id, arr)
  }
  // Newest group first; spans within a group oldest-first (so reading top-down = chronological).
  const ordered = Array.from(groups.entries())
    .map(([id, spans]) => ({
      id,
      spans: spans.slice().sort((a, b) => a.id - b.id),
      latest: spans.reduce((acc, s) =>
        new Date(s.created_at).getTime() > new Date(acc.created_at).getTime() ? s : acc
      ),
    }))
    .sort((a, b) => new Date(b.latest.created_at).getTime() - new Date(a.latest.created_at).getTime())

  return (
    <Container maxWidth="lg" py="2xl">
      <VStack gap="lg" fullWidth>
        <VStack gap="sm">
          <Heading level={2}>Traces</Heading>
          <Text color="muted">
            Activité brute de l'agent : appels LLM, outils, recall, erreurs.
            Refresh toutes les 5 s.
          </Text>
        </VStack>

        <HStack gap="xs">
          <Button
            variant={!errorsOnly ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setErrorsOnly(false)}
          >
            Toutes
          </Button>
          <Button
            variant={errorsOnly ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setErrorsOnly(true)}
          >
            Erreurs uniquement
          </Button>
        </HStack>

        {tracesQuery.error && (
          <Banner variant="error" title="Échec du chargement">
            {String((tracesQuery.error as Error).message)}
          </Banner>
        )}

        {tracesQuery.isLoading && (
          <HStack gap="sm" align="center">
            <Spinner size="sm" />
            <Text size="sm" color="muted">Chargement…</Text>
          </HStack>
        )}

        {!tracesQuery.isLoading && ordered.length === 0 && (
          <Banner variant="info">Aucune trace pour l'instant.</Banner>
        )}

        <VStack gap="lg" fullWidth>
          {ordered.map(({ id, spans }) => (
            <VStack key={id} gap="xs" fullWidth>
              <HStack justify="between" align="center" gap="sm">
                <Text size="xs" color="muted">trace {id.slice(0, 8)}</Text>
                <Text size="xs" color="muted">
                  {formatRelative(spans[spans.length - 1]?.created_at ?? new Date().toISOString())}
                  {' · '}
                  {spans.length} span(s)
                </Text>
              </HStack>
              <VStack gap="xs" fullWidth>
                {spans.map((s) => {
                  const io = (s.input != null || s.output != null) ? (
                    <Text
                      size="xs"
                      color="muted"
                      style={{
                        fontFamily: 'ui-monospace, monospace',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {s.input != null ? `in ${compactJson(s.input)}` : ''}
                      {s.input != null && s.output != null ? '  ' : ''}
                      {s.output != null ? `out ${compactJson(s.output)}` : ''}
                    </Text>
                  ) : null

                  const variant: BannerVariant = s.error ? 'error' : KIND_VARIANT[s.span_kind]
                  return (
                    <Banner
                      key={s.id}
                      variant={variant}
                      size="sm"
                      title={`${s.span_kind} · ${s.name} · ${formatDuration(s.duration_ms)}`}
                    >
                      <VStack gap="xs" align="start">
                        {s.error && <Text size="xs">{s.error}</Text>}
                        {io}
                      </VStack>
                    </Banner>
                  )
                })}
              </VStack>
            </VStack>
          ))}
        </VStack>
      </VStack>
    </Container>
  )
}
